// INTELIMOVE-SAAS-004 (IM-ADR-022): e-mail transacional. Só modelos fechados, ligados a eventos autorizados (convite,
// redefinição de senha, mudanças de acesso, entrada na administração da plataforma, teste de conexão). Não há envio livre.
import { z } from 'zod';

export const MAIL_TEMPLATES = [
  'ORGANIZATION_INVITATION', 'INVITATION_REVOKED', 'PASSWORD_RESET', 'PASSWORD_RESET_ASSISTED', 'MEMBER_DEACTIVATED',
  'MEMBER_REACTIVATED', 'MEMBER_REMOVED', 'MEMBER_ROLE_CHANGED', 'OWNERSHIP_TRANSFERRED', 'PLATFORM_ACCESS_ALERT', 'CONNECTION_TEST', 'BILLING_EVENT', 'EMAIL_VERIFICATION',
] as const;
export type MailTemplate = (typeof MAIL_TEMPLATES)[number];
export const MAIL_TEMPLATE_LABEL: Record<MailTemplate, string> = {
  ORGANIZATION_INVITATION: 'Convite', INVITATION_REVOKED: 'Convite encerrado', PASSWORD_RESET: 'Redefinição de senha',
  PASSWORD_RESET_ASSISTED: 'Recuperação assistida', MEMBER_DEACTIVATED: 'Acesso desativado', MEMBER_REACTIVATED: 'Acesso reativado',
  MEMBER_REMOVED: 'Acesso encerrado', MEMBER_ROLE_CHANGED: 'Perfil alterado', OWNERSHIP_TRANSFERRED: 'Propriedade transferida',
  PLATFORM_ACCESS_ALERT: 'Entrada na administração', CONNECTION_TEST: 'Teste de conexão', BILLING_EVENT: 'Evento financeiro', EMAIL_VERIFICATION: 'Verificação de e-mail',
};

export const MAIL_STATUSES = ['PENDENTE', 'ENVIANDO', 'ENVIADO', 'FALHOU', 'CANCELADO'] as const;
export type MailStatus = (typeof MAIL_STATUSES)[number];
/** Rótulos para o cliente: "enviado" quer dizer aceito pelo provedor, não lido nem entregue na caixa. */
export const MAIL_STATUS_LABEL: Record<MailStatus, string> = {
  PENDENTE: 'Envio pendente', ENVIANDO: 'Enviando', ENVIADO: 'Enviado', FALHOU: 'Não foi possível enviar', CANCELADO: 'Envio cancelado',
};

export const MAIL_PROVIDERS = ['NENHUM', 'SMTP', 'TESTE'] as const;
export type MailProvider = (typeof MAIL_PROVIDERS)[number];
export const MAIL_PROVIDER_LABEL: Record<MailProvider, string> = { NENHUM: 'Não configurado', SMTP: 'SMTP autenticado', TESTE: 'Adaptador de teste (sem envio externo)' };

/**
 * Endereço aceito pelo serviço: o mesmo formato que o banco exige (sem espaço, vírgula, ponto e vírgula, aspas ou <>),
 * o que fecha injeção de cabeçalho e lista de destinatários por um campo de endereço.
 */
export const MAIL_ADDRESS = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/;
export const isMailAddress = (v: string): boolean => v.length <= 254 && MAIL_ADDRESS.test(v);
const Address = z.string().trim().toLowerCase().max(254).refine(isMailAddress, 'Informe um e-mail válido.');

/** Resultado do pedido de envio no momento da operação (não é confirmação de entrega). */
export type MailRequestOutcome = 'ENFILEIRADO' | 'DUPLICADO' | 'INDISPONIVEL' | 'LIMITADO' | 'NAO_SOLICITADO';

export interface InvitationDeliveryDto { status: MailStatus; attempts: number; at: string; sentAt: string | null }
/** Resposta de criar/reemitir convite: o link continua disponível uma vez para copiar; `email` diz o que aconteceu com o envio. */
export interface InvitationEmailDto { outcome: MailRequestOutcome; messageId: string | null }

/** IM-API-399: o tenant só sabe se o envio automático está disponível (nada da configuração). */
export interface TenantCommunicationDto { emailDelivery: boolean }

export interface PlatformMailMessageDto {
  id: string; template: MailTemplate; status: MailStatus; attempts: number; maxAttempts: number; createdAt: string; lastAttemptAt: string | null;
  sentAt: string | null; nextAttemptAt: string | null; failureKind: 'TEMPORARIA' | 'PERMANENTE' | 'CANCELADA' | null; lastError: string | null;
  organization: string | null; recipient: string; sourceType: string; providerMessageId: string | null; reprocessed: number; retryable: boolean;
}
export interface PlatformMailOverviewDto {
  settings: {
    provider: MailProvider; senderEmail: string | null; senderName: string; replyTo: string | null;
    /** Só para PLATFORM_ADMIN (null para o suporte). A senha nunca sai do servidor: só `hasSecret`. */
    smtpHost: string | null; smtpPort: number | null; smtpSecurity: 'TLS' | 'STARTTLS' | null; smtpUsername: string | null;
    hasSecret: boolean; deliveryEnabled: boolean; configVersion: number; updatedAt: string; updatedBy: string | null;
    lastTest: { at: string; ok: boolean; detail: string | null; current: boolean } | null;
  };
  active: boolean;
  /** Chave de cifragem presente no ambiente (sem ela não há como guardar a senha nem montar links). */
  cryptoAvailable: boolean;
  allowedSenderDomains: string[];
  queue: { pending: number; failed: number; retrying: number; sent24h: number; failed24h: number; cancelled24h: number; oldestPendingSeconds: number | null };
  recent: PlatformMailMessageDto[];
  alerts: Array<{ code: string; level: 'critical' | 'warning' | 'info'; message: string }>;
}

const Reason = z.string().trim().min(5, 'Explique o motivo (mínimo de 5 caracteres).').max(500);
const TotpCode = z.string().trim().regex(/^\d{6}$/, 'Informe os 6 dígitos do aplicativo autenticador.');

/** IM-API-394: configuração (PLATFORM_ADMIN, código novo do autenticador). A senha é só de escrita. */
export const MailSettingsRequest = z.object({
  provider: z.enum(['NENHUM', 'SMTP']),
  senderEmail: Address.nullable().default(null),
  senderName: z.string().trim().min(1).max(80).regex(/^[^\r\n<>"]+$/, 'Nome de exibição sem quebras de linha, aspas ou <>.').default('InteliMove'),
  replyTo: Address.nullable().default(null),
  smtpHost: z.string().trim().toLowerCase().regex(/^[a-z0-9.-]{1,253}$/, 'Servidor inválido.').nullable().default(null),
  smtpPort: z.number().int().min(1).max(65535).nullable().default(null),
  smtpSecurity: z.enum(['TLS', 'STARTTLS']).nullable().default(null),
  smtpUsername: z.string().trim().max(254).regex(/^[^\r\n]*$/).nullable().default(null),
  /** Nova senha/chave do provedor. Ausente = manter a atual (`keepSecret`). Nunca é devolvida. */
  smtpPassword: z.string().min(1).max(512).optional(),
  keepSecret: z.boolean().default(true),
  reason: Reason,
  code: TotpCode,
}).strict().superRefine((v, ctx) => {
  if (v.provider !== 'SMTP') return;
  for (const k of ['senderEmail', 'smtpHost', 'smtpPort', 'smtpSecurity'] as const) {
    if (v[k] === null) ctx.addIssue({ code: 'custom', path: [k], message: 'Obrigatório para SMTP.' });
  }
});
export type MailSettingsInput = z.infer<typeof MailSettingsRequest>;

/** IM-API-395: CONNECTION = só conecta e autentica; MESSAGE = envia uma mensagem de teste ao próprio administrador. */
export const MailTestRequest = z.object({ mode: z.enum(['CONNECTION', 'MESSAGE']).default('CONNECTION') }).strict();
export interface MailTestResultDto { ok: boolean; detail: string; mode: 'CONNECTION' | 'MESSAGE' }

/** IM-API-396: ligar/desligar o envio externo. Ligar exige teste aprovado da configuração atual. */
export const MailDeliveryRequest = z.object({ enabled: z.boolean(), reason: Reason, code: TotpCode }).strict();
/** IM-API-397: reprocessar uma falha (no máximo 3 vezes por mensagem). */
export const MailRetryRequest = z.object({ reason: Reason }).strict();
/** IM-API-398: recuperação assistida. `confirm` = e-mail da pessoa, digitado. */
export const PlatformPasswordRecoveryRequest = z.object({ reason: Reason, confirm: z.string().trim().min(3).max(254), code: TotpCode }).strict();

/** Alertas operacionais da comunicação (puro; mesmo cálculo para API e testes). */
export function mailAlerts(o: Pick<PlatformMailOverviewDto, 'settings' | 'active' | 'queue' | 'cryptoAvailable'>): PlatformMailOverviewDto['alerts'] {
  const a: PlatformMailOverviewDto['alerts'] = [];
  if (!o.cryptoAvailable) a.push({ code: 'crypto', level: 'critical', message: 'Chave de cifragem ausente no ambiente: não é possível guardar credenciais nem montar links.' });
  if (o.settings.provider === 'NENHUM') a.push({ code: 'not_configured', level: 'warning', message: 'Nenhum provedor de e-mail configurado: convites seguem por link copiado e a recuperação de senha por e-mail fica indisponível.' });
  else if (!o.settings.deliveryEnabled) a.push({ code: 'delivery_off', level: 'warning', message: 'Envio externo desligado: nenhuma mensagem é enviada até que um administrador o ligue.' });
  if (o.settings.provider === 'SMTP' && !(o.settings.lastTest?.ok && o.settings.lastTest.current)) a.push({ code: 'test_required', level: 'warning', message: 'A configuração atual ainda não tem teste de conexão aprovado.' });
  if (o.queue.failed > 0) a.push({ code: 'failures', level: 'warning', message: `${o.queue.failed} mensagem(ns) com falha definitiva.` });
  if (o.active && o.queue.oldestPendingSeconds !== null && o.queue.oldestPendingSeconds > 900) a.push({ code: 'backlog', level: 'warning', message: 'Há mensagens pendentes há mais de 15 minutos: confira o worker e o provedor.' });
  return a;
}
